// src/components/common.js — extracted from App.js
import React, { useState } from "react";
import { LOGO_B64 } from "../constants";
import { aiSearch } from "../lib/utils";

// ─── Logo ─────────────────────────────────────────────────────────────────────
export function Logo({ size="md", onClick, light=false }) {
  const h = size==="sm" ? 48 : size==="lg" ? 90 : 64;
  return (
    <div onClick={onClick} style={{cursor:onClick?"pointer":"default",display:"inline-flex",alignItems:"center",userSelect:"none"}}>
      <img
        src={`data:image/png;base64,${LOGO_B64}`}
        alt="The Horological Catalog"
        style={{height:h, width:"auto", filter:light?"brightness(0) invert(1)":"none"}}
      />
    </div>
  );
}

// ─── UPI Payment Modal ────────────────────────────────────────────────────────

// ─── WatchSketch ──────────────────────────────────────────────────────────────
export function WatchSketch({ cx=100, cy=100, r=80, opacity=0.07, rotate=0 }) {
  const ticks = [0,30,60,90,120,150,180,210,240,270,300,330];
  return (
    <g transform={`rotate(${rotate} ${cx} ${cy})`} opacity={opacity} stroke="#1A1612" fill="none">
      <rect x={cx-r*0.38} y={cy-r*1.45} width={r*0.76} height={r*0.48} rx="5" strokeWidth="1.2"/>
      <rect x={cx-r*0.38} y={cy+r*0.97} width={r*0.76} height={r*0.48} rx="5" strokeWidth="1.2"/>
      <circle cx={cx} cy={cy} r={r} strokeWidth="1.5"/>
      <circle cx={cx} cy={cy} r={r*0.9} strokeWidth="0.6"/>
      {ticks.map((d,i)=>{
        const a=(d-90)*Math.PI/180, r1=r*0.88, r2=(i%3===0?r*0.76:r*0.82);
        return <line key={d} x1={cx+r1*Math.cos(a)} y1={cy+r1*Math.sin(a)} x2={cx+r2*Math.cos(a)} y2={cy+r2*Math.sin(a)} strokeWidth={i%3===0?1.5:0.8}/>;
      })}
      <line x1={cx} y1={cy} x2={cx} y2={cy-r*0.58} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1={cx} y1={cy} x2={cx+r*0.38} y2={cy+r*0.22} strokeWidth="1.2" strokeLinecap="round"/>
    </g>
  );
}


// ─── AccessDenied ─────────────────────────────────────────────────────────────
export function AccessDenied({ openAuth }) {
  return (
    <div style={{textAlign:"center",padding:"80px 0"}}>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:48,marginBottom:12}}>⊘</div>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,marginBottom:8}}>Access restricted.</div>
      <p style={{color:"var(--ink3)",marginBottom:24}}>You need to sign in with the right role to view this page.</p>
      <button className="btn-ink" onClick={openAuth}>Sign in →</button>
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────

// ─── MaintenanceScreen ────────────────────────────────────────────────────────
export function MaintenanceScreen({ message, openAuth, children }) {
  return (
    <div style={{minHeight:"100vh",background:"#111118",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
      <div style={{marginBottom:48}}><Logo size="md" light/></div>
      {/* Animated watch movement — pure CSS SVG */}
      <svg width="120" height="120" viewBox="0 0 120 120" style={{marginBottom:32}}>
        <style>{`
          @keyframes rotate-slow { from{transform-origin:60px 60px;transform:rotate(0deg)} to{transform-origin:60px 60px;transform:rotate(360deg)} }
          @keyframes rotate-medium { from{transform-origin:60px 60px;transform:rotate(0deg)} to{transform-origin:60px 60px;transform:rotate(360deg)} }
          @keyframes tick { 0%,100%{opacity:1} 50%{opacity:.3} }
          .gear-outer{animation:rotate-slow 8s linear infinite}
          .gear-inner{animation:rotate-medium 3s linear infinite reverse}
          .second-hand{transform-origin:60px 70px;animation:rotate-slow 60s steps(60) infinite}
          .beat{animation:tick 1s ease-in-out infinite}
        `}</style>
        {/* Outer gear */}
        <g className="gear-outer">
          {[...Array(16)].map((_,i)=>{
            const a=(i/16)*Math.PI*2; const r1=54,r2=58;
            return <line key={i} x1={60+r1*Math.cos(a)} y1={60+r1*Math.sin(a)} x2={60+r2*Math.cos(a)} y2={60+r2*Math.sin(a)} stroke="#B8860B" strokeWidth="3" strokeLinecap="round"/>;
          })}
          <circle cx="60" cy="60" r="50" fill="none" stroke="#B8860B" strokeWidth="1.5" opacity="0.4"/>
        </g>
        {/* Dial */}
        <circle cx="60" cy="60" r="44" fill="#1A1612" stroke="#2E2720" strokeWidth="1"/>
        {/* Hour markers */}
        {[...Array(12)].map((_,i)=>{
          const a=(i/12)*Math.PI*2-Math.PI/2;
          return <line key={i} x1={60+36*Math.cos(a)} y1={60+36*Math.sin(a)} x2={60+40*Math.cos(a)} y2={60+40*Math.sin(a)} stroke="#B8860B" strokeWidth={i%3===0?2:1} strokeLinecap="round"/>;
        })}
        {/* Inner gear */}
        <g className="gear-inner">
          <circle cx="60" cy="60" r="18" fill="none" stroke="#D97706" strokeWidth="1" opacity="0.5"/>
          {[...Array(8)].map((_,i)=>{
            const a=(i/8)*Math.PI*2;
            return <line key={i} x1={60+14*Math.cos(a)} y1={60+14*Math.sin(a)} x2={60+18*Math.cos(a)} y2={60+18*Math.sin(a)} stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>;
          })}
        </g>
        {/* Hands */}
        <line x1="60" y1="60" x2="60" y2="28" stroke="#F0E8D8" strokeWidth="2" strokeLinecap="round"/>
        <line x1="60" y1="60" x2="78" y2="64" stroke="#F0E8D8" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="60" y1="60" x2="60" y2="24" stroke="#B8860B" strokeWidth="1" strokeLinecap="round" className="second-hand"/>
        {/* Centre jewel */}
        <circle cx="60" cy="60" r="3" fill="#B8860B" className="beat"/>
      </svg>

      <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,color:"#F0E8D8",marginBottom:10,textAlign:"center"}}>Under Maintenance</div>
      <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#B8860B",letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:16}}>The Horological Catalog</div>
      <p style={{fontSize:14,color:"#6B7280",textAlign:"center",maxWidth:380,lineHeight:1.7,margin:"0 0 32px"}}>{message || "We're making improvements to the platform. Back shortly."}</p>
      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#3F3F46",letterSpacing:"0.12em"}}>THC.WATCH · INDIA</div>
      {openAuth && (
        <button onClick={openAuth} style={{marginTop:48,background:"none",border:"1px solid #2E2720",borderRadius:6,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:10,color:"#6B7280",letterSpacing:"0.12em",padding:"6px 16px"}}>
          admin access
        </button>
      )}
      {children}
    </div>
  );
}

// ─── ADMIN ─────────────────────────────────────────────────────────────────────

// ─── ErrorBoundary ───────────────────────────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:32,fontFamily:"monospace",background:"#fff1f2",color:"#c0392b",borderRadius:12,margin:24,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>
          <strong>Admin panel error (share this with dev):</strong>{"\n\n"}
          {this.state.error?.message}{"\n"}{this.state.error?.stack}
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── APP ──────────────────────────────────────────────────────────────────────

// ─── WatchCard ───────────────────────────────────────────────────────────────
export function WatchCard({ watch:w, sellerName, sellerVerified }) {
  return (
    <div className="card fade-in" style={{opacity:w.available?1:.55}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
        <div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.15em",marginBottom:4,textTransform:"uppercase"}}>{w.brand} · {w.decade}</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,lineHeight:1.1}}>{w.model}</div>
          <div style={{fontSize:9.5,color:"#8A7E74",fontFamily:"'DM Mono',monospace",marginTop:2}}>Ref. {w.reference}</div>
        </div>
        <span className={`tag ${w.available?"tag-grn":"tag-red"}`}>{w.available?"Available":"Sold"}</span>
      </div>
      <p style={{fontSize:12.5,color:"#4A423A",lineHeight:1.65,marginBottom:14}}>{w.description}</p>
      {sellerName && (
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12,padding:"8px 12px",background:"var(--bg2)",borderRadius:10}}>
          <span style={{fontSize:12,color:"#4A423A"}}>By <strong>{sellerName}</strong></span>
          {sellerVerified && <span className="vbadge">✦ Trusted</span>}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#B8860B"}}>₹{w.price.toLocaleString("en-IN")}</div>
        <span className="tag">{w.condition}</span>
      </div>
    </div>
  );
}

// ─── Seller Card ──────────────────────────────────────────────────────────────

// ─── SellerCard ──────────────────────────────────────────────────────────────
export function SellerCard({ seller:s, onClick }) {
  const stars="★".repeat(Math.round(s.rating||0))+"☆".repeat(5-Math.round(s.rating||0));
  return (
    <div className="card" onClick={onClick} style={{cursor:onClick?"pointer":"default"}}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12}}>
        {s.avatarUrl ? <img src={s.avatarUrl} className="avatar" style={{objectFit:"cover",flexShrink:0}} alt={s.avatar}/> : <div className="avatar">{s.avatar}</div>}
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:3}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700}}>{s.name}</div>
            {s.verified && <span className="vbadge" style={s.badge==="Top Seller"?{background:"#FFF0F5",borderColor:"#F4AACA",color:"#9B2C5C"}:{}}>✦ {s.badge||"Trusted"}</span>}
          </div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9.5,color:"#8A7E74"}}>{s.handle} · {s.city}</div>
        </div>
      </div>
      <p style={{fontSize:12.5,color:"#4A423A",lineHeight:1.6,marginBottom:12}}>{s.bio}</p>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div><span style={{color:"#B8860B",fontSize:13}}>{stars}</span><span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",marginLeft:6}}>{s.rating} ({s.reviewCount})</span></div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>{s.sales} sales</span>
        </div>
      </div>
    </div>
  );
}

// ─── SEARCH ───────────────────────────────────────────────────────────────────

// ─── SearchView ──────────────────────────────────────────────────────────────
export function SearchView({ inventory }) {
  const [q,setQ]     = useState("");
  const [load,setL]  = useState(false);
  const [res,setRes] = useState(null);
  const [mat,setMat] = useState(null);
  const [filt,setF]  = useState("All");

  const doSearch = async () => {
    if (!q.trim()) return;
    setL(true); setRes(null); setMat(null);
    const r = await aiSearch(q,inventory);
    setRes(r); setMat(inventory.filter(w=>r.matchedIds?.includes(w.id)));
    setL(false);
  };

  const shown = res?(mat||[]):inventory.filter(w=>filt==="All"||w.brand===filt);

  return (
    <div className="fade-in">
      <div style={{marginBottom:32}}><span className="sl">AI Search</span><h2 style={{fontFamily:"'Playfair Display',serif",fontSize:40,letterSpacing:"-0.025em"}}>Find your watch.</h2></div>
      <div style={{display:"flex",gap:10,marginBottom:28}}>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doSearch()} placeholder="HMT Pilot black dial 70s under ₹3k  ·  Allwyn quartz date  ·  Ricoh auto deadstock" style={{flex:1}}/>
        <button className="btn-ink" onClick={doSearch} disabled={load} style={{minWidth:100,whiteSpace:"nowrap"}}>{load?"Scanning…":"Search →"}</button>
      </div>
      {load && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74",letterSpacing:"0.2em"}}>SEARCHING…</div>}
      {res && (
        <div style={{background:"#fff",border:"1.5px solid var(--bd)",borderLeft:"4px solid var(--gold)",borderRadius:6,padding:18,marginBottom:22}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}><span className="sl" style={{marginBottom:0}}>AI Match</span><span className={`tag ${res.confidence==="high"?"tag-grn":res.confidence==="medium"?"tag-gold":"tag-red"}`}>{res.confidence?.toUpperCase()}</span></div>
          <p style={{fontSize:14,lineHeight:1.7,marginBottom:res.sourcing_note?10:0}}>{res.summary}</p>
          {res.sourcing_note && <p style={{fontSize:12,color:"#8A7E74",fontStyle:"italic",borderTop:"1px solid #EAE3D8",paddingTop:8,marginTop:8}}>Sourcing: {res.sourcing_note}</p>}
        </div>
      )}
      {!res && (
        <div style={{display:"flex",gap:8,marginBottom:22,flexWrap:"wrap"}}>
          {["All","HMT","Allwyn","Ricoh"].map(b=>(
            <button key={b} onClick={()=>setF(b)} style={{background:filt===b?"#1A1612":"#fff",color:filt===b?"#F0E8D8":"#4A423A",border:"1.5px solid",borderColor:filt===b?"#1A1612":"var(--bd)",padding:"7px 18px",fontSize:12,borderRadius:10}}>{b}</button>
          ))}
          <span style={{marginLeft:"auto",fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74",alignSelf:"center"}}>{shown.filter(w=>w.available).length} available</span>
        </div>
      )}
      {shown.length>0
        ?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(252px,1fr))",gap:14}}>{shown.map(w=><WatchCard key={w.id} watch={w}/>)}</div>
        :!load&&<div style={{textAlign:"center",padding:56,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No match — request it and we'll source it.</div>}
    </div>
  );
}

// ─── REQUEST ──────────────────────────────────────────────────────────────────
